/**
 * Was eine Fingerbewegung bedeutet — als Rechnung, nicht als Ereignis.
 *
 * Gesten sind der Teil einer App, den man nicht sieht und sofort merkt, wenn
 * er fehlt: vom linken Rand zurück, ein Blatt nach unten wegschieben, lange
 * auf eine Zeile drücken statt erst in einen Auswahlmodus zu wechseln. Die
 * Schwellen dafür sind Erfahrungswerte und gehören an eine Stelle, an der man
 * sie nachlesen und prüfen kann — nicht verstreut in drei Ereignisbehandlern.
 *
 * Kein DOM, keine Zeitmessung: Alles kommt als Zahl herein.
 */

/** Nur der linke Rand löst „zurück" aus — sonst kollidiert es mit Scrollen. */
export const RANDBREITE = 28;

/** Unter dieser Strecke ist eine Bewegung ein Tippen mit unruhiger Hand. */
export const WACKELN = 10;

/**
 * Beginnt hier eine Zurück-Geste?
 *
 * Nur am linken Rand, und nur, wenn die Seite überhaupt einen Weg zurück hat.
 * Die zweite Bedingung steht hier, damit sie nicht im Ereignisbehandler
 * vergessen wird: Ein Wisch, der nichts tut, fühlt sich kaputt an.
 */
export function beginntZurueck(startX, hatZurueck) {
  return Boolean(hatZurueck) && startX <= RANDBREITE;
}

/**
 * Ist die Bewegung waagerecht genug, um sie zu übernehmen?
 *
 * Solange das nicht feststeht, darf die Seite weiterscrollen. Erst wenn der
 * Finger deutlich mehr quer als längs gelaufen ist, gehört ihm die Geste.
 */
export function istWaagerecht(dx, dy) {
  return dx > WACKELN && Math.abs(dx) > Math.abs(dy) * 1.4;
}

/**
 * Wird beim Loslassen zurückgegangen?
 *
 * Zwei Wege führen dorthin: weit genug gezogen (ein Fünftel der Breite) oder
 * schnell genug geschnippt. Ohne den zweiten Weg müsste man auf einem breiten
 * Telefon quer über den halben Schirm ziehen, und die Geste fühlte sich zäh
 * an.
 */
export function wischentscheid({ dx = 0, dy = 0, dauer = 0, breite = 390 } = {}) {
  if (!istWaagerecht(dx, dy)) return false;
  if (dx >= Math.max(70, breite * 0.2)) return true;
  const tempo = dauer > 0 ? dx / dauer : 0;
  return dx > 36 && tempo > 0.45;
}

/**
 * Wird das Blatt beim Loslassen geschlossen?
 *
 * Dieselbe Logik eine Achse weiter: ein Viertel der Blatthöhe oder ein
 * schneller Schubs nach unten. Nach oben gezogen wird nichts geschlossen,
 * deshalb zählt nur positives `dy`.
 */
export function blattentscheid({ dy = 0, dauer = 0, hoehe = 600 } = {}) {
  if (dy <= 0) return false;
  if (dy >= Math.max(90, hoehe * 0.25)) return true;
  const tempo = dauer > 0 ? dy / dauer : 0;
  return dy > 40 && tempo > 0.5;
}

/**
 * Wie weit folgt das Blatt dem Finger?
 *
 * Nach unten eins zu eins — was man schiebt, bewegt sich. Nach oben wird
 * gebremst, sonst hinge das Blatt über seinem eigenen Rand und sähe kaputt
 * aus. Der Widerstand ist derselbe, den jede Liste am Ende ihres Laufs zeigt.
 */
export function zugweg(dy) {
  return dy >= 0 ? dy : -Math.sqrt(-dy) * 3;
}

/** Millisekunden, nach denen aus Drücken ein Langdruck wird. */
export const LANGDRUCK_MS = 450;

/**
 * Zählt das noch als Langdruck?
 *
 * Wer den Finger bewegt, will scrollen, nicht auswählen. Die Prüfung läuft
 * während des Drückens, deshalb kommt sie mit der bisher gelaufenen Strecke
 * aus.
 */
export function haeltStill(dx, dy) {
  return Math.abs(dx) <= WACKELN && Math.abs(dy) <= WACKELN;
}
